import type { DataSourceListingDto } from 'cadenza/api-client/dto/data-source-dto';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { DataSourceCapability } from 'cadenza/model/data-source-capability.gen';
import type { DataSourceType } from 'cadenza/repository/model/data-source-type.gen';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';

/**
 * Check if the given DataSourceType supports CMDB.
 * This is (and should be) used only during creation of the data source, where we have only the
 * selected type, otherwise the capability 'CADENZA_DB' should be checked.
 *
 * @param type - The data source type to check
 */
export function supportsCmdb(type?: DataSourceType) {
  return type === 'RDBMS';
}

/**
 * Check if the given DataSourceType supports automatic creation of basic objecttypes.
 * This is (and should be) used only during creation of the data source, where we have only the
 * selected type, otherwise the capability 'OBJECTTYPE_COMPATIBLE' should be checked.
 *
 * @param type - The data source type to check
 */
export function supportsAutobot(type?: DataSourceType) {
  return type === 'RDBMS' || type === 'WFS' || type === 'ELASTIC' || type === 'ARCGIS_REST';
}

/**
 * Check if a datasource has the given capability.
 *
 * @param dataSource - The data source to check
 * @param capability - The capability to check
 * @return `true` if the datasource has the given capability
 */
export function hasCapability(
  dataSource: WorkbookDataSourceDto | DataSourceListingDto | undefined,
  capability: DataSourceCapability,
): boolean {
  return dataSource?.capabilities?.includes(capability) ?? false;
}

export function isNotCmdbOrCmdbDataSourceInRepositoryWithoutDPC(
  dataSource: DataSourceListingDto,
  repository?: RepositoryDto,
) {
  return (
    dataSource.cmdbState !== 'CMDB' ||
    (dataSource.cmdbState === 'CMDB' && repository?.dataProtectionMode !== 'contexts')
  );
}
